import type { HomeData } from '@blockbattle/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Clapperboard, Zap } from 'lucide-react';
import { useEffect, useState } from 'react';
import { trackAnalytics } from '../analytics.js';
import { api } from '../api.js';
import { playGameSound } from '../audio.js';
import { formatCountdown } from '../home-state.js';
import { playGameHaptic } from '../haptics.js';
import { useAppStore } from '../store.js';

export function RewardedEnergyButton({
  ads,
  className = '',
}: {
  readonly ads: HomeData['energy']['ads'];
  readonly className?: string;
}) {
  const adapter = useAppStore((state) => state.adapter);
  const haptics = useAppStore((state) => state.haptics);
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!ads.resetAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [ads.resetAt]);

  if (adapter?.platform !== 'vk' || !ads.available) return null;

  const watch = async (): Promise<void> => {
    if (pending || ads.remaining <= 0) return;
    try {
      setPending(true);
      setStatus('Готовим рекламу…');
      trackAnalytics('energy_rewarded_ad_started', { remaining: ads.remaining });
      const session = await api.startRewardedEnergyAd();
      if (!adapter.showRewardedAd) throw new Error('Реклама недоступна в этой версии VK');
      const shown = await adapter.showRewardedAd();
      if (!shown.completed) {
        setStatus('Сейчас нет доступной рекламы. Попробуйте немного позже.');
        trackAnalytics('energy_rewarded_ad_failed', { reason: 'not_completed' });
        return;
      }
      const reward = await api.completeRewardedEnergyAd(session.claimId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['home'] }),
        queryClient.invalidateQueries({ queryKey: ['path'] }),
      ]);
      setStatus(`+${reward.reward} энергия начислена. Теперь у вас ${reward.current}.`);
      trackAnalytics('energy_rewarded_ad_completed', {
        energy: reward.current,
        remaining: reward.remaining,
      });
      playGameSound('energy');
      playGameHaptic(adapter, haptics, 'energy');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'Не удалось начислить энергию.');
      trackAnalytics('energy_rewarded_ad_failed', { reason: 'request_failed' });
      playGameHaptic(adapter, haptics, 'error');
    } finally {
      setPending(false);
    }
  };

  const reset = formatCountdown(ads.resetAt, now);
  return (
    <div className={`rewarded-energy ${className}`.trim()}>
      <button
        className="secondary-button rewarded-energy-button"
        type="button"
        disabled={pending || ads.remaining <= 0}
        onClick={() => void watch()}
      >
        <Clapperboard aria-hidden="true" />
        {pending ? 'Идёт реклама…' : `Смотреть рекламу · +${ads.rewardPerView}`}
        <Zap aria-hidden="true" />
      </button>
      <small>
        {ads.remaining > 0
          ? `Доступно ${ads.remaining} из ${ads.limitPer24Hours} просмотров за 24 часа`
          : reset
            ? `Лимит исчерпан · новый просмотр через ${reset}`
            : 'Лимит просмотров за 24 часа исчерпан'}
      </small>
      {status ? (
        <small className="rewarded-energy-status" aria-live="polite">
          {status}
        </small>
      ) : null}
    </div>
  );
}
